import { useMemo } from "react";
import { Trash2 } from "lucide-react";
import {
  FormField,
  TextareaField,
  DatePickerField,
  SelectField,
} from "@/components/common";
import { useTran } from "@/hooks";
import {
  generateYearOptions,
  formatYear,
  getDisclosureYearValueOptions,
} from "@/features/esg/utils/year-helpers";
import {
  getFieldList,
  INPUT_FIELD_WIDTH,
  FIELD_LABELS,
  REQUIRED_FIELDS,
} from "../../domains/dataEntry.validation";
import { InfoDot } from "@/components/ui/tooltip";

const YEAR_OPTIONS = generateYearOptions().map((y) => ({
  value: String(y),
  label: formatYear(y),
}));

const EntryRowTable = ({
  entries,
  inputType = "value",
  unitOptions = [],
  errors,
  showInlineErrors = false,
  onUpdate,
  onRemove,
  masterDataYear,
  selectedReportingYear,
  disclosure = null,
  isEdit = false,
}) => {
  const { t } = useTran();
  const fields = getFieldList(inputType);

  const valueYearOptions = useMemo(
    () => getDisclosureYearValueOptions(disclosure),
    [disclosure],
  );
  const fieldClassName = showInlineErrors ? "space-y-1" : "space-y-0";

  const gridTemplateColumns =
    fields.map((f) => INPUT_FIELD_WIDTH[f] || "1fr").join(" ") + " auto";

  const renderCell = (entry, index, field) => {
    const fieldErrors = errors?.get?.(entry.id);
    const error = fieldErrors?.[field];

    if (field === "reportingMonth") {
      return (
        <DatePickerField
          value={entry.reportingMonth || null}
          onChange={(value) => onUpdate(entry.id, "reportingMonth", value)}
          placeholder={t("Date")}
          year={masterDataYear ? Number(masterDataYear) : undefined}
          className={fieldClassName}
          error={error}
          disabled={!masterDataYear}
          reportingYear={selectedReportingYear}
          isEdit={isEdit}
        />
      );
    }

    if (field === "entryDate") {
      return (
        <DatePickerField
          value={entry[field] || null}
          onChange={(value) => onUpdate(entry.id, field, value)}
          placeholder={t("Date")}
          className={fieldClassName}
          error={error}
        />
      );
    }

    if (field === "year") {
      return (
        <SelectField
          options={YEAR_OPTIONS}
          value={entry.year || ""}
          onValueChange={(value) => onUpdate(entry.id, "year", value)}
          placeholder={t("Year")}
          error={error}
          className={fieldClassName}
        />
      );
    }

    if (field === "unit") {
      return (
        <SelectField
          options={unitOptions}
          value={entry.unit || ""}
          onValueChange={(value) => onUpdate(entry.id, "unit", value)}
          placeholder={t("Select unit")}
          disabled={unitOptions.length === 0}
          error={error}
          className={fieldClassName}
        />
      );
    }

    if (field === "value" && valueYearOptions.length > 0) {
      return (
        <SelectField
          options={valueYearOptions}
          value={entry[field] || ""}
          onValueChange={(value) => onUpdate(entry.id, field, value)}
          placeholder={t("Select year")}
          error={error}
          className={fieldClassName}
        />
      );
    }

    if (field === "value" && inputType === "simpleLongText") {
      return (
        <TextareaField
          value={entry[field] ?? ""}
          onChange={(e) => onUpdate(entry.id, field, e.target.value)}
          placeholder={t("Response")}
          className={fieldClassName}
          textareaClassName="text-sm min-h-[4rem] resize-y"
          error={error}
        />
      );
    }

    if (field === "value" && inputType === "selector") {
      return (
        <SelectField
          options={unitOptions}
          value={entry[field] || ""}
          onValueChange={(value) => onUpdate(entry.id, field, value)}
          placeholder={t("Select")}
          disabled={unitOptions.length === 0}
          error={error}
          className={fieldClassName}
        />
      );
    }

    // The value cell is text for qualitative input types and numeric for
    // every other quantitative type. simpleLongText / selector are handled
    // by the earlier branches above, so only "simple" remains here as a
    // textual variant of the value field.

    const isQualitativeValue = field === "value" && inputType === "simple";

    return (
      <FormField
        value={entry[field] ?? ""}
        onChange={(e) => onUpdate(entry.id, field, e.target.value)}
        placeholder={
          field === "label"
            ? ""
            : //? t("Entry {{index}}", { index: index + 1 })
              t(FIELD_LABELS[field] || field)
        }
        type={field === "value" && !isQualitativeValue ? "number" : "text"}
        inputClassName="h-9 text-sm"
        className={fieldClassName}
        error={error}
      />
    );
  };

  const FIELD_TOOLTIP_MESSAGES = {
    remark: "Please enter “n.a” when the required data is not available.",
  };

  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-x-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground min-w-[600px]"
        style={{ gridTemplateColumns }}
      >
        {fields.map((field) => {
          const label =
            field === "value" && inputType === "simpleLongText"
              ? t("Response")
              : t(FIELD_LABELS[field] || field);
          const isRequired = (REQUIRED_FIELDS[inputType] || []).includes(field);
          const tooltipMessage = FIELD_TOOLTIP_MESSAGES[field];
          return (
            <span key={field}>
              {label}
              {isRequired && <span className="text-destructive ml-0.5">*</span>}
              {tooltipMessage && (
                <span className="ml-0.5">
                  <InfoDot tip={tooltipMessage} />
                </span>
              )}
            </span>
          );
        })}
        <span className="opacity-0">
          <Trash2 size={15} />
        </span>
      </div>

      {entries.map((entry, i) => (
        <div
          key={entry.id}
          className="grid gap-x-3 items-start px-3 py-2 min-w-[600px]"
          style={{ gridTemplateColumns }}
        >
          {fields.map((field) => (
            <div
              key={field}
              className={
                showInlineErrors
                  ? undefined
                  : "[&_p.text-destructive]:hidden [&_small.text-destructive]:hidden [&_[role=alert]]:hidden"
              }
            >
              {renderCell(entry, i, field)}
            </div>
          ))}
          <button
            type="button"
            onClick={() => onRemove(entry.id)}
            className="p-1 text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default EntryRowTable;
